// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import {
    HoffmanManufacturerAvailabilty,
    PanduitManufacturerAvailabilty,
    RockwellManufacturerAvailabiltyModel
} from "@mcmc/App/Services/ManufacturerAvailabiltyService";
import { Draft } from "immer";

interface ManufacturerAvailabiltyDto {
    name: string;
    date?: string;
    qty: number;
    manufacturerItem?: string;
    formattedQty?: string;
}

interface ManufacturerAvailabiltyDataViews {
    [key: string]: {
        value?: ManufacturerAvailabiltyDto[];
        isLoading: boolean;
    };
}

export type ManufacturerAvailabilityState = {
    dataViews: ManufacturerAvailabiltyDataViews;
};

const manufacturerAvailabilityInitialState: ManufacturerAvailabilityState = {
    dataViews: {},
};

const xManufacturerAvailabilityReducer = createTypedReducerWithImmer(manufacturerAvailabilityInitialState, {
    "xManufacturerAvailabilities/BeginLoading": (
        draft: Draft<ManufacturerAvailabilityState>,
        action: { productIds: string[] },
    ) => {
        draft.dataViews[action.productIds.join("")] = { isLoading: true };
    },
    "xManufacturerAvailabilities/CompleteLoadingRockwellInventory": (
        draft: Draft<ManufacturerAvailabilityState>,
        action: { productIds: string[]; apiResult: RockwellManufacturerAvailabiltyModel },
    ) => {
        const key = action.productIds.join("");
        draft.dataViews[key].isLoading = false;
        draft.dataViews[key].value = action.apiResult?.rockwellProductInventoryList
            ?.reduce<ManufacturerAvailabiltyDto[]>((current, rockwellProductInventory) => {
                rockwellProductInventory.inventories.forEach(inv => {
                    current.push({
                        name: rockwellProductInventory.city,
                        qty: inv.availableQuantity,
                        manufacturerItem: inv.catalogId,
                    });
                });
                return current;
            }, [])
            .sort((a, b) => a.name.localeCompare(b.name));
    },
    "xManufacturerAvailabilities/CompleteLoadingHoffmanInventory": (
        draft: Draft<ManufacturerAvailabilityState>,
        action: { productIds: string[]; apiResult: HoffmanManufacturerAvailabilty },
    ) => {
        const key = action.productIds.join("");
        draft.dataViews[key].isLoading = false;
        draft.dataViews[key].value = action.apiResult?.manufacturerLocation
            ?.map(location => ({
                name: location.manufacturerLocation,
                qty: location.availability,
                date: action.apiResult.bestShipDate,
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
    },
    "xManufacturerAvailabilities/CompleteLoadingPanduitInventory": (
        draft: Draft<ManufacturerAvailabilityState>,
        action: { productIds: string[]; apiResult: PanduitManufacturerAvailabilty },
    ) => {
        const key = action.productIds.join("");
        draft.dataViews[key].isLoading = false;
        draft.dataViews[key].value = action.apiResult?.panduitInventory.inventory
            ?.map(location => ({
                name: `${location.partLeadTime}`,
                qty: location.pkgQty,
                formattedQty: `${location.partStockLevel} ${location.partStockLevelUom}`,
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
    },
});

(reducers as any).xManufacturerAvailability = xManufacturerAvailabilityReducer;

type xManufacturerAvailabilityReducers = {
    manufacturerAvailability: typeof xManufacturerAvailabilityReducer;
};

export type xManufacturerAvailabilityActions =
    | Parameters<xManufacturerAvailabilityReducers[keyof xManufacturerAvailabilityReducers]>[1]
    | AnyAction;
